import gql from 'graphql-tag';
import { useState, useContext, useEffect } from 'react';
import { AccountContext } from '../components/AccountContext';
import { useQuery } from '@apollo/react-hooks';
import { CustomFunctionality } from '../shared/Constants';
import { defineMessages, useIntl } from 'react-intl';

const CHECK_CUSTOM_FUNCTIONALITIES = gql`
  query checkCustomFunctionalitiesQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      availableCustomFunctionalities
    }
  }
`;

const messages = defineMessages({
  INDIVIDUAL: { id: 'report.individual', defaultMessage: 'Individual Report' },
  CC_INTERESTS: { id: 'report.CC.interests', defaultMessage: 'Career Counseling - Interests' },
  CC_PERSONALITY: { id: 'report.CC.personality', defaultMessage: 'Career Counseling - Personality' },
  SUMMARY: { id: 'report.summary', defaultMessage: 'Summary Report' },
  INTERVIEW: { id: 'report.interview', defaultMessage: 'Interview Report' },
  MANAGEMENT: { id: 'report.management', defaultMessage: 'Management Report' },
  COUNSELORS_INTERESTS_REPORT: { id: 'report.counselors-interests', defaultMessage: 'Counselor Interests Report' },
  STUDENTS_INTERESTS_REPORT: { id: 'report.students-interests', defaultMessage: 'Students Interests Report' },
  INTERESTS_GRAPH_REPORT: { id: 'report.interests-graph', defaultMessage: 'Interests Graph Report' },
  SIMPLIFIED_CC_INTERESTS: {
    id: 'report.simplified-CC.interests',
    defaultMessage: 'Simplified Career Counseling - Interests',
  },
  SIMPLIFIED_CC_PERSONALITY: {
    id: 'report.simplified-CC.personality',
    defaultMessage: 'Simplified Career Counseling - Personality',
  },
});

const useCustomFunctionalityCheck = () => {
  const [availableCustomFunctionalities, setAvailableCustomFunctionalities] = useState([]);

  const currentAccount = useContext(AccountContext);
  const { formatMessage } = useIntl();

  const { data } = useQuery(CHECK_CUSTOM_FUNCTIONALITIES, { variables: { accountId: currentAccount } });

  const { account } = data;

  useEffect(() => {
    if (account) {
      setAvailableCustomFunctionalities(account.availableCustomFunctionalities);
    }
  }, [currentAccount, account]);

  const doesAccountHaveAccessToCustomFunctionality = (customFunctionality) => {
    return availableCustomFunctionalities.includes(customFunctionality);
  };

  const getReportsToAutomaticallyGenerate = () => {
    const AUTO_GENERATE_REPORTS = [
      {
        id: 'SUMMARY',
        name: 'report.summary',
      },
      {
        id: 'INTERVIEW',
        name: 'report.interview',
      },
      {
        id: 'MANAGEMENT',
        name: 'report.management',
      },
      {
        id: 'INDIVIDUAL',
        name: 'report.individual',
      },
      {
        id: 'CC_INTERESTS',
        name: 'report.CC.interests',
      },
      {
        id: 'CC_PERSONALITY',
        name: 'report.CC.personality',
      },
    ];

    const accountCanGenerateCounselorsInterestsReport = doesAccountHaveAccessToCustomFunctionality(
      CustomFunctionality.COUNSELORS_INTERESTS_REPORT,
    );
    const accountCanGenerateStudentsInterestsReport = doesAccountHaveAccessToCustomFunctionality(
      CustomFunctionality.STUDENTS_INTERESTS_REPORT,
    );
    const accountCanGenerateInterestsGraphReport = doesAccountHaveAccessToCustomFunctionality(
      CustomFunctionality.INTERESTS_GRAPH_REPORT,
    );
    const accountCanGenerateSimplifiedCcReports = doesAccountHaveAccessToCustomFunctionality(
      CustomFunctionality.SIMPLIFIED_CC_REPORTS,
    );

    if (accountCanGenerateCounselorsInterestsReport) {
      AUTO_GENERATE_REPORTS.push({
        id: 'COUNSELORS_INTERESTS_REPORT',
        name: 'report.counselors-interests',
      });
    }

    if (accountCanGenerateStudentsInterestsReport) {
      AUTO_GENERATE_REPORTS.push({
        id: 'STUDENTS_INTERESTS_REPORT',
        name: 'report.students-interests',
      });
    }

    if (accountCanGenerateInterestsGraphReport) {
      AUTO_GENERATE_REPORTS.push({
        id: 'INTERESTS_GRAPH_REPORT',
        name: 'report.interests-graph',
      });
    }

    if (accountCanGenerateSimplifiedCcReports) {
      AUTO_GENERATE_REPORTS.push({
        id: 'SIMPLIFIED_CC_INTERESTS',
        name: 'report.simplified-CC.interests',
      });

      AUTO_GENERATE_REPORTS.push({
        id: 'SIMPLIFIED_CC_PERSONALITY',
        name: 'report.simplified-CC.personality',
      });
    }

    return AUTO_GENERATE_REPORTS.map((report) => ({
      value: report.id,
      label: formatMessage(messages[report.id]),
    }));
  };

  return {
    doesAccountHaveAccessToCustomFunctionality,
    getReportsToAutomaticallyGenerate,
  };
};

export default useCustomFunctionalityCheck;
